import React from 'react';
import { connect, useSelector } from 'react-redux';
import { bindActionCreators, compose } from 'redux';

import './ExchangeReview.scss';

import { Address } from '../../Sell/MailACheck/Address';
import config from '../../../config/config';
import ExchangeRetirementIncomeWithholdings from '../ExchangeRetirementIncomeWithholdings/ExchangeRetirementIncomeWithholdings';
import ExchangeSummaryFromAmount from '../ExchangeSummaryFromAmount/ExchangeSummaryFromAmount';
import ExchangeSummaryFundList from '../ExchangeSummaryFundList/ExchangeSummaryFundList';
import ExchangeSummaryToAmount from '../ExchangeSummaryToAmount/ExchangeSummaryToAmount';
import ExchangeWithholdings from '../ExchangeWithholdings/ExchangeWithholdings';
import { moduleActions } from '../../../redux/modules';
import {
  getExchangeIsIRAContribution,
  getExchangeIsTaxable,
  getExchangeFromSummaryFundList,
  getExchangeFromUnit,
  getExchangeFundsWithInterestedPartyFlag,
  getExchangeHasInterestedParty,
  getExchangeShowRIWithholdingDisclosure,
  getExchangeToNewFunds,
  getExchangeToSummaryFundList,
  getExchangeType,
  getHasExchangeFromSweep,
} from '../../../redux/modules/exchangeTransaction/exchangeTransaction.selectors';
import {
  getCurrentContributionYear,
  getPriorContributionYear,
} from '../../../redux/modules/shoppingCart/shoppingCart.selectors';
import {
  isCostBasisApplicable,
  ProductTypes,
  AGREEMENT_TYPES,
} from '../../../settings/settings';
import If from '../../Shared/If/If';
import Note from '../../Shared/Note/Note';
import StepExpanded from '../../Shared/StepExpanded/StepExpanded';
import TRP from '../../Shared/TRP/TRP';
import { agreementTypeToDisplay } from '../../../utils/funds/funds-utility';
import { isCollegeExchange } from '../../../utils/transactions-util';

import {
  isCustomerWebViewEnabled,
  isSalesforceRep,
} from '../../../redux/modules/authentication/authentication.module.selectors';
import { getMandatoryWithholdingStates } from 'src/redux/modules/stateWithholding/selectors';
import { getDisplayTradeDate } from '../../../redux/modules/funds/UserFunds/userFunds.selectors';

const ExchangeReview = ({
  customerWebViewEnabled,
  isSalesforceRep,
  currentContributionYear,
  exchangeContributionYear,
  exchangeIsIRAContribution,
  exchangeIsTaxable,
  exchangeFromSummaryFundList,
  exchangeFromUnit,
  exchangeFundsWithInterestedPartyFlag,
  exchangeHasInterestedParty,
  exchangeShowRIWithholdingDisclosure,
  exchangeToNewFunds,
  exchangeToSummaryFundList,
  exchangeType,
  hasExchangeFromSweep,
  priorContributionYear,
  exchangeTradeDates,
}) => {
  const mandatoryWithholdingStates = useSelector(getMandatoryWithholdingStates);
  let userFundsStates = exchangeFromSummaryFundList.map((fund) => {
    return fund?.address?.state;
  });
  userFundsStates = [...new Set(userFundsStates)];
  return (
    <StepExpanded>
      <div className="exchange-review-summary exchange-review-from">
        <div className="exchange-review-heading">Exchange from</div>
        <ExchangeSummaryFundList
          amountComponent={ExchangeSummaryFromAmount}
          exchangeSummaryFundList={exchangeFromSummaryFundList}
          exchangeUnit={exchangeFromUnit}
          exchangeTradeDates={exchangeTradeDates.fromFunds}
          showTradeDates={
            exchangeFromSummaryFundList.length >
            exchangeToSummaryFundList.length
          }
        />
        <If
          test={
            !isCollegeExchange() &&
            exchangeFromSummaryFundList.some((fund) =>
              isCostBasisApplicable(fund.agreementType)
            )
          }
        >
          <Note title="Please Note:">
            Noncovered shares (shares purchased before 2012) will be redeemed
            before covered shares regardless of the cost basis method selected,
            unless you{' '}
            <a
              href={`${config.url}/personal-investing/help/contact/index.html`}
              target="_blank"
              rel="noopener noreferrer"
            >
              contact us
            </a>{' '}
            to request a redemption using the Specific Lot Identification (SLID)
            method. Once all noncovered shares are redeemed, covered shares will
            be redeemed using the cost basis method selected for the account.
            You should consult your tax professional if you have any questions.
          </Note>
        </If>
        <If test={hasExchangeFromSweep}>
          <Note title="Please Note:">
            You are requesting an exchange from a Brokerage sweep account. Stock
            trades settle in three business days, and unsettled trades are not
            reflected in the available balance. Please consider any unsettled
            trades, as well as open orders that may execute, before performing
            this transaction.
          </Note>
        </If>
        <If test={exchangeHasInterestedParty}>
          <Note title="Please Note:">
            Please note, your
            {exchangeFundsWithInterestedPartyFlag.map((fund, i) => (
              <span key={fund.uniqueKey}>
                {' '}
                {fund.name} {agreementTypeToDisplay(fund.agreementType)} Account{' '}
                {fund.agreementNumber}
                {exchangeFundsWithInterestedPartyFlag.length > 1 &&
                  i < exchangeFundsWithInterestedPartyFlag.length - 1 &&
                  ','}{' '}
              </span>
            ))}
            contains a service feature that mails duplicate confirmations to a
            secondary address. You have requested to transfer to the newly
            created
            {exchangeToNewFunds.map((fund, i) => (
              <span key={fund.uniqueKey}>
                {' '}
                {fund.name} {agreementTypeToDisplay(fund.agreementType)} Account
                {exchangeToNewFunds.length > 1 &&
                  i < exchangeToNewFunds.length - 1 &&
                  ','}{' '}
              </span>
            ))}
            which does not currently include this service feature. If you
            require duplicate confirmations for your
            {exchangeToNewFunds.map((fund, i) => (
              <span key={fund.uniqueKey}>
                {' '}
                {fund.name} {agreementTypeToDisplay(fund.agreementType)} Account
                {exchangeToNewFunds.length > 1 &&
                  i < exchangeToNewFunds.length - 1 &&
                  ','}{' '}
              </span>
            ))}
            to be mailed to a secondary address, please mail a signed letter of
            instruction to the following address:
            <div>
              <TRP /> Account Services
            </div>
            <Address
              address={{
                street1: 'P.O. Box 17300',
                city: 'Baltimore',
                state: 'MD',
                zip: '21297-1300',
              }}
            />
            {!(!customerWebViewEnabled && isSalesforceRep) && (
              <div>
                If you have any questions, please contact a T. Rowe Price
                Representative at 1-800-225-5132 for assistance.
              </div>
            )}
          </Note>
        </If>
      </div>

      <div className="e2e-exchange-to-review exchange-review-summary">
        <div className="exchange-review-heading">Exchange to</div>
        <ExchangeSummaryFundList
          amountComponent={ExchangeSummaryToAmount}
          currentContributionYear={currentContributionYear}
          exchangeContributionYear={exchangeContributionYear}
          exchangeSummaryFundList={exchangeToSummaryFundList}
          exchangeType={exchangeType}
          exchangeUnit={exchangeFromUnit}
          priorContributionYear={priorContributionYear}
          showIRAContributionYear={exchangeIsIRAContribution}
          suppressDollarAmount={true}
          suppressExchangeAmount={exchangeToSummaryFundList.length === 1}
          exchangeTradeDates={exchangeTradeDates.toFunds}
          showTradeDates={
            exchangeFromSummaryFundList.length <=
            exchangeToSummaryFundList.length
          }
        />
      </div>

      <If test={isCollegeExchange()}>
        <div className="e2e-college-exchange-review-note college-exchange-review-note">
          <div className="college-exchange-review-note-container">
            <Note title="">
              <ul>
                <li>
                  <span className="exchange-review-text-emphasis">
                    Please Note: You are permitted to change investment options
                    (portfolios) for a particular beneficiary twice per calendar
                    year.
                  </span>{' '}
                  If you have multiple portfolio accounts for a beneficiary
                  within the plan, all investment changes requested on the same
                  trade date will be considered as one change for the calendar
                  year.
                </li>
              </ul>
              <br></br>
              <ul>
                <li>
                  <span className="exchange-review-text-emphasis">
                    Any automatic monthly contributions on the account you are
                    exchanging from will not be carried over.
                  </span>{' '}
                  You will need to establish new automatic monthly contributions
                  for the receiving account by visiting Automatic Transactions.
                  Additionally, if you have a gifting profile connected to the
                  account you are exchanging from and wish to make changes,
                  visit the gifting tile on your account dashboard. For direct
                  deposit or payroll deduction changes,{' '}
                  {exchangeToSummaryFundList
                    .map((fund) => fund.productType)
                    .includes(ProductTypes.ALASKA529) &&
                    'or to update your Permanent Fund Dividend (PFD) allocations, '}
                  please contact us.
                </li>
              </ul>
            </Note>
          </div>
        </div>
      </If>

      <If test={exchangeIsTaxable}>
        <ExchangeWithholdings
          hasRoth={exchangeFromSummaryFundList
            .map((fund) => fund.agreementType)
            .some(
              (fund) =>
                fund === AGREEMENT_TYPES.ROTH_IRA ||
                fund === AGREEMENT_TYPES.ROTH_ROLLOVER_IRA
            )}
          mandatoryWithholdingStates={mandatoryWithholdingStates}
          userState={userFundsStates}
        />
      </If>
      <If
        test={exchangeShowRIWithholdingDisclosure}
        component={ExchangeRetirementIncomeWithholdings}
      />
    </StepExpanded>
  );
};

const mapStateToProps = (state) => {
  const {
    exchangeTransaction: { exchangeContributionYear },
  } = state;
  return {
    customerWebViewEnabled: isCustomerWebViewEnabled(state),
    isSalesforceRep: isSalesforceRep(state),
    currentContributionYear: getCurrentContributionYear(state),
    exchangeContributionYear,
    exchangeIsIRAContribution: getExchangeIsIRAContribution(state),
    exchangeIsTaxable: getExchangeIsTaxable(state),
    exchangeFromSummaryFundList: getExchangeFromSummaryFundList(state),
    exchangeFromUnit: getExchangeFromUnit(state),
    displayTradeDate: getDisplayTradeDate(state),
    exchangeFundsWithInterestedPartyFlag:
      getExchangeFundsWithInterestedPartyFlag(state),
    exchangeHasInterestedParty: getExchangeHasInterestedParty(state),
    exchangeShowRIWithholdingDisclosure:
      getExchangeShowRIWithholdingDisclosure(state),
    exchangeToNewFunds: getExchangeToNewFunds(state),
    exchangeToSummaryFundList: getExchangeToSummaryFundList(state),
    exchangeType: getExchangeType(state),
    hasExchangeFromSweep: getHasExchangeFromSweep(state),
    priorContributionYear: getPriorContributionYear(state),
  };
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(moduleActions, dispatch),
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  ExchangeReview
);
